<template>
  <div data-dynatrace="product-price" data-test-id="product-pricing">
    <span :class="[brandClasses.wrapper, classWrapper, { 'f-col gap-y-0.5': block }]">
      <template v-if="isRange && !showDiscountedRanges">
        <span
          :class="[
            classCurrent,
            { 'c-red-30': price.highCurrent < price.lowOriginal && !suppressSaleStyling },
          ]"
        >
          <span class="sr-only">{{ $t.price }}:</span>
          {{ current }}
        </span>
      </template>
      <template v-else>
        <component
          :is="isDiscounted ? 'del' : 'span'"
          v-if="(!hideStrikethroughPrice || !isDiscounted) && !suppressSaleStyling"
          v-style:c="originalPriceColor"
          :class="isDiscounted ? [brandClasses.original, classOriginal] : classCurrent"
        >
          <span v-if="isDiscounted" class="sr-only">
            {{ isOmnibus ? $t.previousDiscountedPrice : $t.originalPrice }}:
          </span>
          <span v-else class="sr-only">{{ $t.price }}:</span>
          {{ isOmnibus ? historicBest : original }}
        </component>
        <del
          v-if="price.previousBestSalesPrice && !suppressSaleStyling"
          v-style:c="salePriceColor"
          :class="isDiscounted ? [brandClasses.original, classOriginal] : classCurrent"
        >
          <span class="sr-only">{{ $t.previousDiscountedPrice }}:</span>
          {{ $formatPrice(price.previousBestSalesPrice, currency) }}
        </del>
        <ins
          v-if="isDiscounted && !suppressSaleStyling"
          v-style:c="salePriceColor"
          :class="[classCurrent, { [brandClasses.sale]: !hideStrikethroughPrice }]"
          style="text-decoration: none"
        >
          <span class="sr-only">
            {{ price.previousBestSalesPrice || isOmnibus ? $t.currentDiscountedPrice : $t.discountedPrice }}:
          </span>
          <span class="ws-nowrap" :class="{ block }">
            {{ current }}
            <template v-if="showDiscountPercentage && !isRange">
              ({{ isOmnibus ? historicDiscountPercentage : discountPercentage }})
            </template>
          </span>
        </ins>
        <span v-if="suppressSaleStyling" class="fw-normal" :class="classCurrent">
          <span class="sr-only">{{ $t.price }}:</span>
          {{ current }}
        </span>
      </template>
      <vf-tooltip
        v-if="showSalePriceTooltip && price.salesPriceActiveDate"
        v-style:c="!suppressSaleStyling ? salePriceColor : undefined"
        class="-ml-1"
        :class="{ [brandClasses.sale]: !suppressSaleStyling }"
      >
        <template #trigger>
          <vf-icon name="info" />
        </template>
        {{
          replaceAll(
            $t.salesPriceActiveDateInfo,
            { date: formatDate(new Date(price.salesPriceActiveDate), 'MMMM YYYY') },
          )
        }}
      </vf-tooltip>
    </span>
    <div
      v-if="isOmnibus && showOmnibusBadge"
      :class="brandClasses.omnibusBadge"
    >
      {{ $t.originalPrice }}: {{ original }}
      <vf-toggletip placement="top-start">
        <template #trigger>
          <vf-icon name="info" />
        </template>
        {{ $t.omnibusTooltip }}
      </vf-toggletip>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { RecommendationStyle } from '#content/contexts/recommendation'
import type { CSSClass } from '#types/common'
import type { PriceFull } from '#types/product'
import { RecommendationStyleContextKey } from '#content/contexts/recommendation'
import { formatDate } from '@vueuse/core'

export type Props = {
  /**
   * Price to display
   * Example of strike through sales price range and current price range.
   *
   * $20-$40 - Original Price range: - strike through
   * $10-$30 - Current Range.
   * $20 - lowOriginal
   * $40 - highOriginal
   * $10 - lowCurrent
   * $30 - highCurrent
   */
  price: PriceFull
  /**
   * Currency of the price
   */
  currency: string
  /**
   * Classes to be applied to the current price
   */
  classCurrent?: CSSClass
  /**
   * Classes to be applied to the original (strikethrough) price, overriding `brandClasses.original`
   * for this instance only
   */
  classOriginal?: CSSClass
  /**
   * Classes to be applied to the wrapper element
   */
  classWrapper?: CSSClass
  /**
   * Sets displaying the discount amount as percentage
   */
  showDiscountPercentage?: boolean
  /**
   * Shows box with original price and omnibus tooltip below sale price.
   */
  showOmnibusBadge?: boolean
  /**
   * Show original price and discounted price below it
   */
  block?: boolean
  hideStrikethroughPrice?: boolean
  isDigitalProduct?: boolean
  showOmnibusDetails?: boolean
  showSalePriceTooltip?: boolean
}

const { classOriginal, currency, isDigitalProduct, price } = defineProps<Props>()

const { brandClasses, showDiscountedRanges } = useAppConfig().components.product.pricing
const { $feature, $formatPrice, $t } = useNuxtApp()

const { originalPriceColor, salePriceColor } = inject(RecommendationStyleContextKey, {} as RecommendationStyle)

const formatDiscount = (percentage: number) => `${$feature.showNegativeDiscountPercentageOnCatalog ? '-' : ''}${percentage}%`

const formatPrice = (low: number, high: number) => isDigitalProduct || low === high
  ? low === 0 ? $t.free : $formatPrice(low, currency)
  : `${$formatPrice(low, currency)} - ${$formatPrice(high, currency)}`

const current = computed(() => formatPrice(price.lowCurrent, price.highCurrent))
const original = computed(() => formatPrice(price.lowOriginal, price.highOriginal))
const historicBest = computed(() => $formatPrice(price.historicBestPrice ?? 0, currency))

const discountPercentage = computed(() => {
  const percentage = getPercentage(price.highOriginal, price.highOriginal - price.lowCurrent, 0)
  return formatDiscount(percentage)
})
const historicDiscountPercentage = computed(() => price.percentagePriceDiscount ? formatDiscount(price.percentagePriceDiscount) : '')

const isDiscounted = computed(() => current.value !== original.value)
const isOmnibus = computed(() => $feature.showOmnibusPricing
  && price.historicBestPrice
  && price.lowCurrent < price.lowOriginal
  && price.lowCurrent < price.historicBestPrice)
const isRange = computed(() => price.lowCurrent !== price.highCurrent)

// If Omnibus info is available but current price is not below the 30-day lowest,
// suppress sale/red styling and treat price as non-discounted for display.
const suppressSaleStyling = computed(
  () => $feature.showOmnibusPricing && price.historicBestPrice && price.lowCurrent >= price.historicBestPrice
)
</script>
